/**
 * ChatWidget - DM Chat Panel
 * 
 * Simple DM chat that opens when clicking on a user in PresenceAvatars.
 * No separate presence tracking - uses existing PresenceContext.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";

// ============================================================
// THEME
// ============================================================
const THEME = {
  bg: "#0a0a0f",
  bgElevated: "#12121a",
  bgCard: "#1a1a24",
  accent: "#2fd6b5",
  textPrimary: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  border: "rgba(255, 255, 255, 0.1)",
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  panel: {
    position: "fixed",
    bottom: 96,
    right: 24,
    width: 360,
    height: 480,
    backgroundColor: THEME.bgElevated,
    borderRadius: 16,
    boxShadow: "0 10px 50px rgba(0, 0, 0, 0.5)",
    border: `1px solid ${THEME.border}`,
    overflow: "hidden",
    zIndex: 9998,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "16px 20px",
    borderBottom: `1px solid ${THEME.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: THEME.textPrimary,
    fontFamily: "'Space Grotesk', sans-serif",
    margin: 0,
  },
  closeButton: {
    background: "transparent",
    border: "none",
    color: THEME.textSecondary,
    fontSize: 20,
    cursor: "pointer",
    padding: "4px 8px",
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  message: {
    display: "flex",
    gap: 8,
    maxWidth: "80%",
  },
  messageOwn: {
    alignSelf: "flex-end",
    flexDirection: "row-reverse",
  },
  messageOther: {
    alignSelf: "flex-start",
  },
  messageBubble: {
    padding: "8px 12px",
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.4,
    fontFamily: "'Space Grotesk', sans-serif",
  },
  messageBubbleOwn: {
    backgroundColor: THEME.accent,
    color: THEME.bg,
    borderBottomRightRadius: 4,
  },
  messageBubbleOther: {
    backgroundColor: THEME.bgCard,
    color: THEME.textPrimary,
    borderBottomLeftRadius: 4,
  },
  messageTime: {
    fontSize: 10,
    color: THEME.textMuted,
    marginTop: 2,
  },
  inputArea: {
    padding: "12px 16px",
    borderTop: `1px solid ${THEME.border}`,
    display: "flex",
    gap: 10,
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: 20,
    border: `1px solid ${THEME.border}`,
    backgroundColor: THEME.bgCard,
    color: THEME.textPrimary,
    fontSize: 13,
    fontFamily: "'Space Grotesk', sans-serif",
    outline: "none",
  },
  sendButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: THEME.accent,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    textAlign: "center",
    color: THEME.textMuted,
  },
  errorBanner: {
    padding: "8px 12px",
    backgroundColor: "rgba(239, 68, 68, 0.2)",
    color: "#ef4444",
    fontSize: 12,
    textAlign: "center",
  },
  offlineBanner: {
    padding: "10px 14px",
    backgroundColor: "rgba(251, 191, 36, 0.15)",
    borderBottom: `1px solid rgba(251, 191, 36, 0.2)`,
    fontSize: 12,
    color: "#fbbf24",
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
  },
  offlineIcon: {
    flexShrink: 0,
    marginTop: 1,
  },
  disclaimer: {
    padding: "12px 16px",
    backgroundColor: "rgba(47, 214, 181, 0.08)",
    borderRadius: 10,
    margin: "12px 0",
    fontSize: 12,
    color: THEME.textSecondary,
    lineHeight: 1.5,
  },
  disclaimerIcon: {
    display: "inline",
    marginRight: 6,
  },
  emojiButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s ease",
  },
  emojiPicker: {
    position: "absolute",
    bottom: 44,
    left: 0,
    width: 240,
    padding: 8,
    backgroundColor: THEME.bgCard,
    borderRadius: 12,
    border: `1px solid ${THEME.border}`,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
    display: "grid",
    gridTemplateColumns: "repeat(8, 1fr)",
    gap: 2,
    zIndex: 10,
  },
  emojiItem: {
    width: 28,
    height: 28,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s ease",
  },
};

// ============================================================
// HELPERS
// ============================================================
function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return "now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Convert URLs and emails to clickable links
function linkifyText(text) {
  if (!text) return text;
  
  // Split by URLs first
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Combined regex for URLs and emails
  const combinedRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    
    // Add the link
    if (match[1]) {
      // URL
      parts.push({ type: 'url', content: match[1] });
    } else if (match[2]) {
      // Email
      parts.push({ type: 'email', content: match[2] });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }
  
  // If no links found, return original text
  if (parts.length === 0) return text;
  
  return parts;
}

// Render message with clickable links
function MessageContent({ text, isOwn }) {
  const parts = linkifyText(text);
  
  // If string returned (no links), just render text
  if (typeof parts === 'string') {
    return <>{parts}</>;
  }
  
  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'url') {
          return (
            <a
              key={i}
              href={part.content}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: isOwn ? '#0a0a0f' : '#2fd6b5',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {part.content}
            </a>
          );
        }
        if (part.type === 'email') {
          return (
            <a
              key={i}
              href={`mailto:${part.content}`}
              style={{
                color: isOwn ? '#0a0a0f' : '#2fd6b5',
                textDecoration: 'underline',
              }}
            >
              {part.content}
            </a>
          );
        }
        return <span key={i}>{part.content}</span>;
      })}
    </>
  );
}

// Common emojis grid
const EMOJI_LIST = [
  '👍', '👎', '❤️', '😊', '😂', '🎉', '🔥', '✅',
  '👀', '🙏', '💪', '👏', '🤔', '😅', '🚀', '⭐',
  '📝', '📎', '📸', '🎬', '🏈', '🏟️', '📺', '🎯',
];

// ============================================================
// CHAT WIDGET
// ============================================================
export function ChatWidget() {
  const { user, userName, userAvatar, userEmail } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null); // User we're chatting with
  const [chatUserOnline, setChatUserOnline] = useState(true); // Is chat user currently online?
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Listen for incoming DMs (even when chat is closed)
  useEffect(() => {
    if (!user || !supabase) {
      console.log("[chat] Skipping DM listener - no user or supabase");
      return;
    }

    console.log("[chat] Setting up incoming DM listener for user:", user.id);

    // Request notification permission on mount
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => console.log("[chat] Notification permission:", p));
    }

    // Store original title
    const originalTitle = document.title;
    let titleInterval = null;

    // Subscribe to all messages where user is a participant
    const channel = supabase
      .channel('my-dms')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const newMsg = payload.new;
          console.log("[chat] Realtime INSERT received:", newMsg);
          
          // Ignore our own messages
          if (newMsg.sender_id === user.id) {
            console.log("[chat] Ignoring own message");
            return;
          }
          
          console.log("[chat] Checking participant status for conversation:", newMsg.conversation_id);
          
          // Check if we're a participant in this conversation
          const { data: participant, error: partErr } = await supabase
            .from('chat_participants')
            .select('user_id')
            .eq('conversation_id', newMsg.conversation_id)
            .eq('user_id', user.id)
            .single();
          
          console.log("[chat] Participant check:", { participant, error: partErr?.message });
          
          if (participant) {
            // We're in this conversation - open the chat!
            console.log("[chat] Opening chat for incoming message from:", newMsg.sender_name);
            setError(null);
            setMessages([]);
            setChatUser({
              id: newMsg.sender_id,
              name: newMsg.sender_name,
              avatar: newMsg.sender_avatar,
            });
            setConversationId(newMsg.conversation_id);
            setIsOpen(true);
            
            // Flash browser tab title
            if (document.hidden) {
              let isOriginal = true;
              titleInterval = setInterval(() => {
                document.title = isOriginal ? `💬 ${newMsg.sender_name}: "${newMsg.message.slice(0, 20)}..."` : originalTitle;
                isOriginal = !isOriginal;
              }, 1000);
              
              // Stop flashing when tab becomes visible
              const handleVisibility = () => {
                if (!document.hidden) {
                  clearInterval(titleInterval);
                  document.title = originalTitle;
                  document.removeEventListener('visibilitychange', handleVisibility);
                }
              };
              document.addEventListener('visibilitychange', handleVisibility);
            }
            
            // Browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`Message from ${newMsg.sender_name}`, {
                body: newMsg.message.slice(0, 100),
                icon: newMsg.sender_avatar || '/favicon.ico',
                tag: 'venueflow-chat',
              });
            }
            
            // Play notification sound
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkZWTjHhpXWBqeImVm5eNfGtiYWp3iJWbmJCBcGVjanuKl5yXjn1tYmJqeYmXnJiRgXFmY2p7ipecl49+bmNjaniJl5yYkYFxZmNqe4qXnJePfm5jY2p4iZecmJGBcWZjanuKl5yXj35uY2NqeImXnJiRgXFmY2p7ipecl49+bmNjaniJl5yYkYFxZmNqe4qXnJePfm5jY2p4iZecmJGBcWZjanuKl5yXj35uY2NqeImXnJiRgXFmY2p7ipecl49+bmNjaniJl5yYkQ==');
              audio.volume = 0.3;
              audio.play().catch(() => {});
            } catch (e) {}
          } else {
            console.log("[chat] Not a participant, ignoring message");
          }
        }
      )
      .subscribe((status, err) => {
        console.log("[chat] DM channel subscription:", status, err || "");
      });

    return () => {
      console.log("[chat] Cleaning up DM listener");
      supabase.removeChannel(channel);
      if (titleInterval) clearInterval(titleInterval);
      document.title = originalTitle;
    };
  }, [user]);

  // Listen for startDM events from PresenceAvatars
  useEffect(() => {
    const handleStartDM = async (event) => {
      const { authUserId, userName: otherName, userAvatar: otherAvatar, isOnline } = event.detail;
      
      console.log("[chat] Start DM requested:", event.detail);
      
      if (!authUserId) {
        console.error("[chat] No authUserId in event, cannot start DM");
        setError("Cannot start chat - user ID not available");
        setIsOpen(true);
        return;
      }
      
      setChatUser({
        id: authUserId,
        name: otherName,
        avatar: otherAvatar,
      });
      setChatUserOnline(isOnline !== false); // Default to true if not specified
      setIsOpen(true);
      setLoading(true);
      setError(null);
      setMessages([]);
      
      try {
        // Get or create DM conversation
        const { data, error: rpcError } = await supabase.rpc("get_or_create_dm", {
          other_user_id: authUserId,
        });
        
        if (rpcError) {
          console.error("[chat] Error creating DM:", rpcError);
          setError("Failed to start chat: " + rpcError.message);
          return;
        }
        
        console.log("[chat] DM conversation ID:", data);
        setConversationId(data);
      } catch (err) {
        console.error("[chat] Error:", err);
        setError("Failed to start chat");
      } finally {
        setLoading(false);
      }
    };
    
    window.addEventListener("venueflow:startDM", handleStartDM);
    return () => window.removeEventListener("venueflow:startDM", handleStartDM);
  }, []);

  // Load messages when conversationId changes
  useEffect(() => {
    if (!conversationId || !supabase) return;

    const loadMessages = async () => {
      const { data, error: fetchError } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(100);

      if (fetchError) {
        console.error("[chat] Error loading messages:", fetchError);
        setError("Failed to load messages");
      } else {
        setMessages(data || []);
        setTimeout(scrollToBottom, 100);
      }
    };

    loadMessages();

    // Subscribe to new messages
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          console.log("[chat] New message from realtime:", payload.new);
          // Add message if not already present (avoid duplicates from optimistic update)
          setMessages((prev) => {
            // Remove any temp messages and add the real one
            const filtered = prev.filter((m) => !m.id.startsWith('temp-'));
            // Check if this message already exists
            if (filtered.some((m) => m.id === payload.new.id)) {
              return filtered;
            }
            return [...filtered, payload.new];
          });
          setTimeout(scrollToBottom, 100);
        }
      )
      .subscribe((status) => {
        console.log("[chat] Realtime subscription status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, scrollToBottom]);

  // Send message
  const handleSend = async () => {
    if (!inputValue.trim() || !conversationId || !supabase) return;

    const text = inputValue.trim();
    setInputValue("");
    
    // Optimistic update - show message immediately
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: userName || userEmail || "Unknown",
      sender_avatar: userAvatar,
      message: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setTimeout(scrollToBottom, 50);

    const { error: sendError } = await supabase.from("chat_messages").insert({
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: userName || userEmail || "Unknown",
      sender_avatar: userAvatar,
      message: text,
    });

    if (sendError) {
      console.error("[chat] Error sending:", sendError);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
      setInputValue(text);
      setError("Failed to send message");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setConversationId(null);
    setChatUser(null);
    setChatUserOnline(true);
    setMessages([]);
    setError(null);
    setShowEmojiPicker(false);
  };

  if (!user || !supabase) return null;

  // Only render panel when open (no floating button)
  if (!isOpen) return null;

  return (
    <div style={styles.panel}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2fd6b5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {chatUser?.name || "Loading..."}
          <span style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: chatUserOnline ? '#22c55e' : '#71717a',
            marginLeft: 8,
            verticalAlign: 'middle',
          }} title={chatUserOnline ? 'Online' : 'Offline'} />
        </h3>
        <button style={styles.closeButton} onClick={handleClose}>
          ✕
        </button>
      </div>

      {/* Error banner */}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Offline warning banner */}
      {!chatUserOnline && (
        <div style={styles.offlineBanner}>
          <span style={styles.offlineIcon}>⚠️</span>
          <span>
            <strong>{chatUser?.name?.split(' ')[0] || 'User'}</strong> appears to be offline. 
            They may not see your message right away.
          </span>
        </div>
      )}

      {/* Messages area */}
      <div style={styles.messagesArea}>
        {loading ? (
          <div style={styles.emptyState}>
            <p style={{ margin: 0 }}>Loading...</p>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.disclaimer}>
              <span style={styles.disclaimerIcon}>💡</span>
              Chat is great for quick questions. For time-sensitive requests, 
              email or call your contacts directly to ensure a timely response.
            </div>
            <p style={{ margin: 0, fontSize: 14, marginTop: 16 }}>
              No messages yet. Say hi!
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isOwn = msg.sender_id === user.id;
              return (
                <div
                  key={msg.id}
                  style={{
                    ...styles.message,
                    ...(isOwn ? styles.messageOwn : styles.messageOther),
                  }}
                >
                  <div>
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(isOwn ? styles.messageBubbleOwn : styles.messageBubbleOther),
                      }}
                    >
                      <MessageContent text={msg.message} isOwn={isOwn} />
                    </div>
                    <div style={{ ...styles.messageTime, textAlign: isOwn ? "right" : "left" }}>
                      {formatTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input area */}
      {conversationId && (
        <div style={styles.inputArea}>
          {/* Emoji picker */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              style={styles.emojiButton}
              title="Add emoji"
            >
              😊
            </button>
            {showEmojiPicker && (
              <div style={styles.emojiPicker}>
                {EMOJI_LIST.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      setInputValue((prev) => prev + emoji);
                      setShowEmojiPicker(false);
                    }}
                    style={styles.emojiItem}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            placeholder="Type a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={styles.input}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            style={{
              ...styles.sendButton,
              opacity: inputValue.trim() ? 1 : 0.5,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={THEME.bg} strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default ChatWidget;
